import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

const scrollLockOwners = new Set();
let lockedScrollY = 0;
let preservedBodyStyles;

function lockPageScroll(owner) {
  if (scrollLockOwners.has(owner)) return;

  if (scrollLockOwners.size === 0) {
    lockedScrollY = window.scrollY;
    preservedBodyStyles = {
      width: document.body.style.width,
      position: document.body.style.position,
      top: document.body.style.top,
    };

    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${lockedScrollY}px`;
  }

  scrollLockOwners.add(owner);
}

function unlockPageScroll(owner) {
  if (!scrollLockOwners.delete(owner) || scrollLockOwners.size > 0) return;

  const bodyStyles = preservedBodyStyles ?? { width: '', position: '', top: '' };
  const previousScrollBehavior = document.documentElement.style.scrollBehavior;

  document.body.style.width = bodyStyles.width;
  document.body.style.position = bodyStyles.position;
  document.body.style.top = bodyStyles.top;
  document.documentElement.style.scrollBehavior = 'auto';
  window.scrollTo(0, lockedScrollY);
  document.documentElement.style.scrollBehavior = previousScrollBehavior;

  preservedBodyStyles = undefined;
}

async function waitForDialogAnimation(dialog) {
  let timeoutId;

  try {
    await Promise.race([
      onAnimationEnd(dialog, undefined, { subtree: false }),
      new Promise((resolve) => {
        timeoutId = window.setTimeout(resolve, 400);
      }),
    ]);
  } catch (error) {
    void error;
  } finally {
    if (timeoutId) window.clearTimeout(timeoutId);
  }
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  #openAnimationFrame = 0;
  #closePromise = null;
  #isClosing = false;

  connectedCallback() {
    super.connectedCallback();

    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
    this.refs.dialog.addEventListener('close', this.#handleNativeClose);

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('cancel', this.#handleCancel);
    this.refs.dialog.removeEventListener('close', this.#handleNativeClose);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    if (this.#openAnimationFrame) {
      cancelAnimationFrame(this.#openAnimationFrame);
      this.#openAnimationFrame = 0;
    }

    unlockPageScroll(this);

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#openAnimationFrame || this.#closePromise) return;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    this.#openAnimationFrame = requestAnimationFrame(() => {
      this.#openAnimationFrame = 0;
      if (!this.isConnected || dialog.open) return;

      lockPageScroll(this);

      try {
        dialog.showModal();
      } catch (error) {
        unlockPageScroll(this);
        throw error;
      }

      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = () => {
    if (this.#closePromise) return this.#closePromise;

    if (this.#openAnimationFrame) {
      cancelAnimationFrame(this.#openAnimationFrame);
      this.#openAnimationFrame = 0;
      unlockPageScroll(this);
      return Promise.resolve();
    }

    const { dialog } = this.refs;

    if (!dialog.open) {
      unlockPageScroll(this);
      return Promise.resolve();
    }

    this.#closePromise = this.#closeDialog(dialog);
    return this.#closePromise;
  };

  async #closeDialog(dialog) {
    this.#isClosing = true;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    try {
      await waitForDialogAnimation(dialog);
    } finally {
      if (dialog.open) dialog.close();
      dialog.classList.remove('dialog-closing');
      unlockPageScroll(this);

      this.#isClosing = false;
      this.#closePromise = null;
      this.dispatchEvent(new DialogCloseEvent());
    }
  }

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  #handleNativeClose = () => {
    if (this.#isClosing) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    unlockPageScroll(this);
    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
